// ./src/components/features/Vehicle/VehicleCard/VehicleCard.tsx
//
// Complete vehicle card component with responsive layout.
//
// Display modes:
// - Dashboard: Quick action buttons (refuel, expense, etc.) at bottom
// - Management: Edit/Delete via hover overlay (desktop) and swipe (mobile),
//   consistent with RecordCard pattern used across the app
//
// Features:
// - Highlight support (green.0 background for recently added/edited)
// - Desktop hover actions (edit/delete ActionIcons, CSS opacity transition)
// - Mobile swipe actions (SwipeableCard wrapper with edit/delete)
// - Unit toggle (user vs car units) when units differ
// - Responsive sizing (mobile vs desktop)

import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  Stack,
  Box,
  Group,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { getVehicleType, VehicleType } from "@/utils/carHelpers";
import { UnitDisplayMode, compareUnits } from "@/utils/unitDisplayHelpers";

import { Car } from "@/utils/api/CarService";
import { DashboardItem, StatsUnits } from "@/utils/api/DashboardService";
import { ReceiptScanResult } from "@/utils/api/ScannerService";

import { DetectLocationFn } from "@/hooks/useScanner";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppIcon } from "@/components/generic/AppIcon";
import { SwipeableCard, SwipeAction } from "@/components/generic/SwipeableCard";

import { VehicleCardHeader } from "../VehicleCardHeader";
import { VehicleCardOdometer } from "../VehicleCardOdometer";
import { VehicleCardStats } from "../VehicleCardStats";
import {
  VehicleCardQuickActions,
  QuickActionType,
} from "../VehicleCardQuickActions";


// ============================================
// Types
// ============================================

export type VehicleCardVariant = "full" | "compact";

export interface VehicleCardProps {
  /** Car object */
  car: Car;

  /** Dashboard item with all pre-aggregated stats (optional, stats hidden if absent) */
  item?: DashboardItem | null;

  /** Body type ID for vehicle type detection */
  bodyTypeId?: string | number | null;

  /** Card variant */
  variant?: VehicleCardVariant;

  /** Currency for cost display (fallback) */
  currency?: string;

  /** Distance unit (fallback when no stats available) */
  distanceUnit?: string;

  /** Consumption unit (fallback when no stats available) */
  consumptionUnit?: string;

  // ============================================
  // Quick Actions (dashboard mode)
  // ============================================

  /** Whether to show quick action buttons at the bottom */
  showQuickActions?: boolean;

  /** Which quick actions to show */
  quickActions?: QuickActionType[];

  /** Handler for quick action click */
  onQuickAction?: (action: QuickActionType, carId: string) => void;

  // ============================================
  // Edit / Delete Actions (management mode)
  // ============================================

  /** Whether to show edit/delete on hover (desktop) */
  showHoverActions?: boolean;

  /** Whether to enable swipe-to-reveal edit/delete (mobile) */
  swipeable?: boolean;

  /** Handler for edit click */
  onEdit?: (car: Car) => void;

  /** Handler for delete click */
  onDelete?: (car: Car) => void;

  /** Whether delete is in progress for this car */
  isDeleting?: boolean;

  // ============================================
  // Highlight
  // ============================================

  /** Whether the card should be highlighted (recently added/edited) */
  isHighlighted?: boolean;

  // ============================================
  // Swipe Management (mobile only, used by parent list)
  // ============================================

  /** Force close the swipe actions */
  forceClose?: boolean;

  /** Callback when force close completes */
  onForceCloseComplete?: () => void;

  /** Callback when swipe state changes */
  onSwipeStateChange?: (isOpen: boolean) => void;

  // ============================================
  // Header / Scan / Misc
  // ============================================

  /** Handler for card header click (e.g., navigate to car details) */
  onHeaderClick?: (car: Car) => void;

  /** Whether to show the scan receipt button */
  showScanReceipt?: boolean;

  /** Handler for scan receipt completion */
  onScanComplete?: (result: ReceiptScanResult, carId?: string | null) => void;

  /** Optional location detection function for scan pipeline */
  onDetectLocation?: DetectLocationFn;

  /** Custom class name */
  className?: string;
}

// ============================================
// CSS for Desktop Hover Actions
// ============================================

const VEHICLE_CARD_CLASS = "vehicle-card";
const VEHICLE_ACTIONS_CLASS = "vehicle-card__actions";

const VEHICLE_CARD_STYLES = `
  .${VEHICLE_ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }

  @media (hover: hover) {
    .${VEHICLE_CARD_CLASS}:hover .${VEHICLE_ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .${VEHICLE_CARD_CLASS}:focus-within .${VEHICLE_ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }
`;

let vehicleCardStylesInjected = false;
const injectVehicleCardStyles = () => {
  if (vehicleCardStylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-vehicle-card", "");
  style.textContent = VEHICLE_CARD_STYLES;
  document.head.appendChild(style);
  vehicleCardStylesInjected = true;
};

// ============================================
// Helper: select units based on display mode
// ============================================

const selectStatsUnits = (
  mode: UnitDisplayMode,
  inUserUnits?: StatsUnits,
  inCarUnits?: StatsUnits
): StatsUnits | undefined => {
  if (mode === "user") return inUserUnits || inCarUnits;
  return inCarUnits || inUserUnits;
};

// ============================================
// Component
// ============================================

export const VehicleCard: React.FC<VehicleCardProps> = ({
  car,
  item,
  bodyTypeId,
  variant = "full",
  currency = "USD",
  distanceUnit: fallbackDistanceUnit = "km",
  //consumptionUnit: fallbackConsumptionUnit = "l100km",
  showQuickActions = false,
  quickActions,
  onQuickAction,
  showHoverActions = false,
  swipeable = false,
  onEdit,
  onDelete,
  isDeleting = false,
  isHighlighted = false,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
  onHeaderClick,
  showScanReceipt = false,
  onScanComplete,
  onDetectLocation,
  className,
}) => {
  const { t } = useTranslation(["common"]);

  // Inject hover action styles once
  if (showHoverActions) {
    injectVehicleCardStyles();
  }

  // Responsive breakpoint
  const { isMobile } = useAppEnvironment();

  // Unit display mode state (default: user preferences)
  const [unitMode, setUnitMode] = useState<UnitDisplayMode>("user");

  // ============================================
  // Units from dashboard item
  // ============================================

  // Use totalStats for odometer and unit comparison (lifetime values)
  const totalInUserUnits = item?.totalStats?.inUserUnits;
  const totalInCarUnits = item?.totalStats?.inCarUnits;

  // Determine if units differ (should show toggle)
  const unitsDiffer = useMemo(() => {
    if (totalInUserUnits && totalInCarUnits) {
      return compareUnits(totalInUserUnits, totalInCarUnits).differs;
    }
    return false;
  }, [totalInUserUnits, totalInCarUnits]);

  // Get selected units based on mode for odometer display
  const selectedTotalUnits = useMemo(
    () => selectStatsUnits(unitMode, totalInUserUnits, totalInCarUnits),
    [unitMode, totalInUserUnits, totalInCarUnits]
  );

  // Get selected units for current month (for monthly change calculation)
  const selectedCurrentMonthUnits = useMemo(
    () =>
      selectStatsUnits(
        unitMode,
        item?.currentMonthStats?.inUserUnits,
        item?.currentMonthStats?.inCarUnits
      ),
    [unitMode, item?.currentMonthStats]
  );

  // Display units with fallbacks
  const displayDistanceUnit =
    selectedTotalUnits?.distanceUnit || fallbackDistanceUnit;

  const displayVolumeUnit =
    selectedTotalUnits?.volumeUnit || "l";

  // Odometer value from total stats
  const odometer = useMemo(() => {
    if (selectedTotalUnits?.lastOdometer != null) {
      return selectedTotalUnits.lastOdometer;
    }
    return (car as any).odometer || 0;
  }, [selectedTotalUnits, car]);

  // Monthly mileage change from current month stats
  const monthlyChange = useMemo(() => {
    if (!selectedCurrentMonthUnits) return null;
    const start = selectedCurrentMonthUnits.firstOdometer;
    const end = selectedCurrentMonthUnits.lastOdometer;
    if (start != null && end != null && end > start) {
      return end - start;
    }
    return null;
  }, [selectedCurrentMonthUnits]);

  // Toggle handler
  const handleUnitToggle = () => {
    setUnitMode((prev) => (prev === "user" ? "car" : "user"));
  };

  // Determine vehicle type
  const vehicleType: VehicleType = getVehicleType(car, bodyTypeId);

  // Car ID for actions
  const carId = car.id || "";

  // Header click handler
  const handleHeaderClick = onHeaderClick
    ? () => onHeaderClick(car)
    : undefined;

  // Show stats only in full variant with dashboard item
  const shouldShowStats = variant === "full" && !!item;

  // Responsive sizes
  const headerSize = isMobile ? "sm" : "md";
  const odometerSize = isMobile ? "sm" : "md";
  const statsSize = isMobile ? "sm" : "md";
  const actionsSize = isMobile ? "mobile" : "md";
  const padding = isMobile ? "md" : "lg";
  const gap = isMobile ? "sm" : "md";

  // ============================================
  // Edit / Delete Handlers
  // ============================================

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(car);
    },
    [onEdit, car]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(car);
    },
    [onDelete, car]
  );

  // ============================================
  // Swipe Actions (mobile, management mode)
  // ============================================

  const swipeActions = useMemo((): SwipeAction[] => {
    if (!swipeable) return [];

    const actions: SwipeAction[] = [];
    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(car),
        disabled: isDeleting,
      });
    }
    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(car),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }
    return actions;
  }, [swipeable, onEdit, onDelete, car, isDeleting, t]);

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => {
      onSwipeStateChange?.(isOpen);
    },
    [onSwipeStateChange]
  );

  // ============================================
  // Desktop Hover Actions (edit / delete)
  // ============================================

  const hasHoverActions = showHoverActions && (!!onEdit || !!onDelete);

  const hoverActionsEl = hasHoverActions ? (
    <Group
      gap={4}
      wrap="nowrap"
      className={VEHICLE_ACTIONS_CLASS}
      style={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
    >
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="filled"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip
          label={t("common:buttons.delete")}
          withArrow
          position="bottom"
        >
          <ActionIcon
            variant="filled"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  ) : null;

  // ============================================
  // Card Inner Content
  // ============================================

  const cardInner = (
    <Stack gap={gap}>
      {/* Header + Odometer Row */}
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Vehicle Header */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <VehicleCardHeader
            car={car}
            vehicleType={vehicleType}
            bodyTypeId={bodyTypeId}
            size={headerSize}
            onClick={handleHeaderClick}
          />
        </Box>

        {/* Odometer - right aligned */}
        <Box style={{ flexShrink: 0, textAlign: "right" }}>
          <VehicleCardOdometer
            odometer={odometer}
            monthlyChange={monthlyChange}
            unit={displayDistanceUnit}
            size={odometerSize}
            layout="vertical"
            showUnitToggle={unitsDiffer}
            unitMode={unitMode}
            volumeUnit={displayVolumeUnit}
            onUnitToggle={handleUnitToggle}
          />
        </Box>
      </Group>

      {/* Stats Row */}
      {shouldShowStats && item && (
        <VehicleCardStats
          item={item}
          currency={currency}
          vehicleType={vehicleType}
          size={statsSize}
          layout="grid"
        />
      )}

      {/* Quick Actions (dashboard mode) */}
      {showQuickActions && carId && (
        <VehicleCardQuickActions
          carId={carId}
          actions={quickActions}
          size={actionsSize}
          onAction={onQuickAction}
          showScanReceipt={showScanReceipt}
          onScanComplete={onScanComplete}
          onDetectLocation={onDetectLocation}
        />
      )}
    </Stack>
  );

  // ============================================
  // Card Wrapper
  // ============================================

  const cardBg = isHighlighted ? "green.0" : undefined;

  const cardElement = (
    <Card
      shadow="sm"
      padding={padding}
      radius="md"
      withBorder
      bg={cardBg}
      className={`${className || ""} ${hasHoverActions ? VEHICLE_CARD_CLASS : ""}`.trim() || undefined}
      style={{
        position: "relative",
        opacity: isDeleting ? 0.6 : 1,
        pointerEvents: isDeleting ? "none" : "auto",
        transition: "opacity 0.2s ease, background-color 0.3s ease",
      }}
    >
      {hoverActionsEl}
      {cardInner}
    </Card>
  );

  // ============================================
  // Swipeable wrapping (mobile only)
  // ============================================

  if (swipeActions.length > 0) {
    return (
      <>
        {/* Mobile: swipeable card */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={handleSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            <Card
              shadow="sm"
              padding={padding}
              radius="md"
              withBorder
              bg={cardBg}
              style={{
                opacity: isDeleting ? 0.6 : 1,
                pointerEvents: isDeleting ? "none" : "auto",
                transition: "opacity 0.2s ease, background-color 0.3s ease",
              }}
            >
              {cardInner}
            </Card>
          </SwipeableCard>
        </Box>

        {/* Desktop: plain card with hover actions */}
        <Box visibleFrom="sm">
          {cardElement}
        </Box>
      </>
    );
  }

  return cardElement;
};